import { motion } from 'framer-motion'
import { useState } from 'react'
import { BookOpen, Clock, Users, Star, ArrowRight, Search, Play } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { CourseModal } from '@/components/ui/course-modal'
import { courseCategories } from '@/data/mock-data'
import { useNavigate } from 'react-router-dom'

export default function MarketplacePage() {
    const navigate = useNavigate()
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCourse, setSelectedCourse] = useState<typeof courses[0] | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const courses = [
        {
            id: '1',
            title: 'Complete NEET 2025 Preparation - Physics, Chemistry & Biology',
            instructor: 'Dr. Rajesh Kumar (AIIMS Delhi, AIR 23)',
            students: 2500,
            rating: 4.9,
            reviews: 450,
            duration: '12 months',
            lectures: 450,
            price: '₹19,999',
            originalPrice: '₹29,999',
            category: 'Complete Package',
            level: 'All Levels',
            badge: 'Bestseller',
            features: ['Live Classes', 'Doubt Clearing', 'Test Series', 'Study Material'],
            lastUpdated: 'January 2025',
        },
        {
            id: '2',
            title: 'Physics Masterclass - Mechanics & Modern Physics',
            instructor: 'Prof. Anita Sharma (IIT Delhi)',
            students: 1800,
            rating: 4.8,
            reviews: 320,
            duration: '3 months',
            lectures: 120,
            price: '₹7,999',
            originalPrice: '₹12,999',
            category: 'Physics',
            level: 'Intermediate',
            badge: 'Popular',
            features: ['HD Videos', 'Practice Problems', 'Weekly Tests'],
            lastUpdated: 'December 2024',
        },
        {
            id: '3',
            title: 'Organic Chemistry Deep Dive - Complete Guide',
            instructor: 'Dr. Vikram Patel (Gold Medalist)',
            students: 2100,
            rating: 4.9,
            reviews: 510,
            duration: '4 months',
            lectures: 150,
            price: '₹8,999',
            originalPrice: '₹14,999',
            category: 'Chemistry',
            level: 'Advanced',
            badge: 'Trending',
            features: ['Reaction Mechanisms', '3D Models', 'Named Reactions'],
            lastUpdated: 'January 2025',
        },
        {
            id: '4',
            title: 'Biology - Human Physiology & Anatomy Complete',
            instructor: 'Dr. Priya Menon (MBBS, MD)',
            students: 1950,
            rating: 4.7,
            reviews: 380,
            duration: '3 months',
            lectures: 110,
            price: '₹7,499',
            originalPrice: '₹11,999',
            category: 'Biology',
            level: 'All Levels',
            badge: 'New',
            features: ['Diagrams', 'Clinical Cases', 'Memory Techniques'],
            lastUpdated: 'January 2025',
        },
        {
            id: '5',
            title: 'NEET Mock Test Series 2025 - 50 Full Length Tests',
            instructor: 'Future Founder Team',
            students: 3200,
            rating: 4.9,
            reviews: 680,
            duration: '6 months',
            lectures: 50,
            price: '₹5,999',
            originalPrice: '₹9,999',
            category: 'Test Series',
            level: 'All Levels',
            badge: 'Bestseller',
            features: ['NTA Pattern', 'Detailed Solutions', 'Performance Analysis'],
            lastUpdated: 'January 2025',
        },
        {
            id: '6',
            title: 'Crash Course - Last 60 Days Strategy',
            instructor: 'Dr. Amit Singh (AIR 45)',
            students: 1500,
            rating: 4.8,
            reviews: 290,
            duration: '2 months',
            lectures: 80,
            price: '₹9,999',
            originalPrice: '₹15,999',
            category: 'Crash Course',
            level: 'Advanced',
            badge: 'Limited',
            features: ['Rapid Revision', 'High-Yield Topics', 'Exam Strategy'],
            lastUpdated: 'December 2024',
        },
    ]

    const filteredCourses = courses.filter(course => {
        const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory
        const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            course.instructor.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesCategory && matchesSearch
    })

    const handleCourseClick = (course: typeof courses[0]) => {
        setSelectedCourse(course)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setTimeout(() => setSelectedCourse(null), 300)
    }

    const handleEnroll = (e: React.MouseEvent) => {
        e.stopPropagation()
        navigate('/checkout')
    }

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-16 overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto mb-12"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            Student Marketplace
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">Your Learning</span>
                            <span className="block mt-2 gradient-text">Dashboard</span>
                        </h1>
                        <p className="text-xl text-theme-secondary mb-8">
                            Explore and enroll in premium courses to accelerate your NEET preparation
                        </p>

                        {/* Search Bar */}
                        <div className="max-w-2xl mx-auto">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search for courses, topics, or instructors..."
                                    className="w-full pl-12 pr-4 py-4 bg-white/80 dark:bg-slate-900/60 backdrop-blur-md border border-slate-200 dark:border-slate-700/50 rounded-xl text-theme-primary placeholder-slate-400 focus:outline-none focus:border-primary-blue transition-colors"
                                />
                                <button className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-primary-blue hover:bg-blue-600 text-white rounded-lg transition-colors">
                                    <Search className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Categories */}
            <section className="py-6">
                <div className="container mx-auto px-4">
                    <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all duration-300 ${selectedCategory === 'all'
                                ? 'bg-primary-blue text-white'
                                : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            All Courses
                        </button>
                        {courseCategories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.name)}
                                className={`px-4 py-2 rounded-xl font-semibold whitespace-nowrap transition-all duration-300 ${selectedCategory === category.name
                                    ? 'bg-primary-blue text-white'
                                    : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {category.name} ({category.courseCount})
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Courses Grid */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-3xl font-display font-bold text-theme-primary">
                            {filteredCourses.length} Courses Available
                        </h2>
                        <select className="px-4 py-2 bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-lg text-theme-primary focus:outline-none focus:border-primary-blue">
                            <option>Most Popular</option>
                            <option>Highest Rated</option>
                            <option>Newest First</option>
                            <option>Price: Low to High</option>
                            <option>Price: High to Low</option>
                        </select>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCourses.map((course, index) => (
                            <motion.div
                                key={course.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassmorphicCard
                                    className="overflow-hidden hover:scale-105 transition-all duration-300 group h-full flex flex-col cursor-pointer"
                                    onClick={() => handleCourseClick(course)}
                                >
                                    {/* Course Image */}
                                    <div className="relative h-48 bg-gradient-to-br from-primary-blue/20 to-accent-gold/20 flex items-center justify-center">
                                        <BookOpen className="h-16 w-16 text-primary-blue/50" />
                                        {course.badge && (
                                            <span className="absolute top-4 right-4 px-3 py-1 bg-accent-gold text-slate-900 text-xs font-semibold rounded-full">
                                                {course.badge}
                                            </span>
                                        )}
                                        <span className="absolute top-4 left-4 px-3 py-1 bg-primary-blue/90 text-white text-xs font-semibold rounded-full">
                                            {course.level}
                                        </span>
                                    </div>

                                    {/* Course Info */}
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="mb-3">
                                            <span className="text-xs text-primary-blue font-semibold">{course.category}</span>
                                        </div>

                                        <h3 className="text-lg font-display font-semibold text-theme-primary mb-2 group-hover:text-primary-blue transition-colors line-clamp-2">
                                            {course.title}
                                        </h3>
                                        <p className="text-sm text-theme-muted mb-4">{course.instructor}</p>

                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="flex items-center gap-1">
                                                <Star className="h-4 w-4 fill-accent-gold text-accent-gold" />
                                                <span className="text-sm font-semibold text-theme-primary">{course.rating}</span>
                                            </div>
                                            <span className="text-sm text-theme-muted">({course.reviews} reviews)</span>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 mb-4 text-xs text-theme-muted">
                                            <div className="flex items-center gap-1">
                                                <Users className="h-3 w-3" />
                                                <span>{course.students.toLocaleString()}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Play className="h-3 w-3" />
                                                <span>{course.lectures} lectures</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                <span>{course.duration}</span>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {course.features.slice(0, 3).map((feature, idx) => (
                                                <span key={idx} className="px-2 py-1 bg-slate-100 dark:bg-slate-800/50 text-xs text-slate-600 dark:text-slate-300 rounded">
                                                    {feature}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="mt-auto pt-4 border-t border-slate-200 dark:border-slate-700/50">
                                            <div className="flex items-center justify-between mb-3">
                                                <div>
                                                    <span className="text-2xl font-bold gradient-text">{course.price}</span>
                                                    <span className="text-sm text-theme-muted line-through ml-2">{course.originalPrice}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleEnroll}
                                                className="w-full px-4 py-3 bg-primary-blue hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-semibold"
                                            >
                                                Enroll Now
                                                <ArrowRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </GlassmorphicCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Course Modal */}
            <CourseModal
                course={selectedCourse}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </div>
    )
}
